import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Route,
  CheckCircle,
  Warning,
  PlayArrow,
  Stop,
  Refresh,
  ExpandMore,
  Timeline,
  Terrain,
} from '@mui/icons-material';
import { useAPI } from '../../hooks/useAPI';

interface RouteInfo {
  raceId: string;
  raceName?: string;
  stageName?: string;
  stageNumber?: number;
  totalDistance: number;
  totalPoints: number;
  waypoints: number;
  segments: number;
  elevationGain: number;
  elevationLoss: number;
  minElevation: number;
  maxElevation: number;
}

interface ActiveRoute {
  raceId: string;
  isActive: boolean;
  assignedAt: Date;
  assignedBy: string;
}

interface RouteAssignmentProps {
  onRouteAssigned?: (raceId: string | null) => void;
}

const RouteAssignment: React.FC<RouteAssignmentProps> = ({ onRouteAssigned }) => {
  const [availableRoutes, setAvailableRoutes] = useState<RouteInfo[]>([]);
  const [activeRoute, setActiveRoute] = useState<ActiveRoute | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const hasFetched = useRef(false);

  const api = useAPI();

  // Load available routes and current active route
  useEffect(() => {
    if (hasFetched.current) return; // Prevent multiple calls
    hasFetched.current = true;

    const loadRoutes = async () => {
      try {
        setLoading(true);

        // Load available routes using optimized summary endpoint
        const routesResponse = await api.get('/routes/summary');
        const routesData = routesResponse.data.data?.routes || [];

        // Transform route data from enhanced summary API
        const formattedRoutes: RouteInfo[] = routesData.map((route: any) => ({
          raceId: route.raceId,
          raceName: route.raceName,
          stageName: route.stageName,
          stageNumber: route.stageNumber,
          totalDistance: route.totalDistance,
          totalPoints: route.totalPoints || 0,
          waypoints: route.waypoints || 0,
          segments: route.segments || 0,
          elevationGain: route.elevationStats?.gain || 0,
          elevationLoss: route.elevationStats?.loss || 0,
          minElevation: route.elevationStats?.minElevation || 0,
          maxElevation: route.elevationStats?.maxElevation || 0,
        }));

        setAvailableRoutes(formattedRoutes);

        // Check for active route
        try {
          const activeResponse = await api.get('/dashboard/active-route');
          if (activeResponse.data?.data?.activeRoute) {
            setActiveRoute(activeResponse.data.data.activeRoute);
          }
        } catch (error) {
          // No active route set, this is normal
          console.log('No active route currently assigned');
        }
      } catch (error: any) {
        console.error('Error loading routes:', error);
        setMessage({ type: 'error', text: 'Failed to load routes' });
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount


  const handleAssignRoute = async () => {
    if (!selectedRoute) {
      setMessage({ type: 'error', text: 'Please select a route to assign' });
      return;
    }

    try {
      setLoading(true);

      await api.post('/dashboard/assign-route', {
        raceId: selectedRoute,
        assignedBy: 'Dashboard User' // In production, this would be the logged-in user
      });

      const assignedRoute = availableRoutes.find(r => r.raceId === selectedRoute);
      setActiveRoute({
        raceId: selectedRoute,
        isActive: true,
        assignedAt: new Date(),
        assignedBy: 'Dashboard User'
      });

      setMessage({
        type: 'success',
        text: `Route "${assignedRoute?.raceName || selectedRoute}" assigned successfully`
      });

      // Notify parent component
      onRouteAssigned?.(selectedRoute);

    } catch (error: any) {
      console.error('Error assigning route:', error);
      setMessage({ type: 'error', text: 'Failed to assign route' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignRoute = async () => {
    try {
      setLoading(true);

      await api.post('/dashboard/unassign-route');

      setActiveRoute(null);
      setSelectedRoute('');
      setMessage({ type: 'info', text: 'Route unassigned. Race progress tracking disabled.' });

      // Notify parent component
      onRouteAssigned?.(null);

    } catch (error: any) {
      console.error('Error unassigning route:', error);
      setMessage({ type: 'error', text: 'Failed to unassign route' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    window.location.reload(); // Simple refresh for now
  };

  const getActiveRouteInfo = (): RouteInfo | null => {
    if (!activeRoute) return null;
    return availableRoutes.find(r => r.raceId === activeRoute.raceId) || null;
  };

  const activeRouteInfo = getActiveRouteInfo();

  return (
    <Box>
      {/* Message Display */}
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Active Route Status */}
      <Card sx={{ mb: 2, bgcolor: activeRoute ? 'success.dark' : 'warning.dark' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            {activeRoute ? <CheckCircle color="inherit" /> : <Warning color="inherit" />}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" color="inherit">
                {activeRoute ? 'Active Route Assigned' : 'No Route Assigned'}
              </Typography>
              {activeRouteInfo && (
                <Typography variant="body2" color="inherit">
                  {activeRouteInfo.raceName} - Stage {activeRouteInfo.stageNumber}: {activeRouteInfo.stageName}
                </Typography>
              )}
              {!activeRoute && (
                <Typography variant="body2" color="inherit">
                  Race progress tracking is disabled. Assign a route to enable progress calculation.
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              {activeRoute && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={handleUnassignRoute}
                  disabled={loading}
                >
                  Unassign
                </Button>
              )}
              <IconButton onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Route Assignment */}
      <Accordion defaultExpanded={!activeRoute}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Route Assignment</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Select Route</InputLabel>
              <Select
                value={selectedRoute}
                onChange={(e) => setSelectedRoute(e.target.value)}
                label="Select Route"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableRoutes.map((route) => (
                  <MenuItem key={route.raceId} value={route.raceId}>
                    <Stack direction="column" sx={{ width: '100%' }}>
                      <Typography variant="body2">
                        {route.raceName} - Stage {route.stageNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {route.stageName} ({route.totalDistance.toFixed(1)}km)
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRoute && (
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleAssignRoute}
                disabled={loading || selectedRoute === activeRoute?.raceId}
                fullWidth
              >
                {selectedRoute === activeRoute?.raceId ? 'Already Assigned' : 'Assign Route'}
              </Button>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Route Details (if route is selected or active) */}
      {(selectedRoute || activeRoute) && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Route Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(() => {
              const displayRoute = availableRoutes.find(r =>
                r.raceId === (selectedRoute || activeRoute?.raceId)
              );

              if (!displayRoute) return <Typography>Route details not available</Typography>;

              return (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      icon={<Route />}
                      label={`${displayRoute.totalDistance.toFixed(1)} km`}
                      color="primary"
                    />
                    <Chip
                      icon={<Timeline />}
                      label={`${displayRoute.waypoints} waypoints`}
                      color="secondary"
                    />
                    <Chip
                      icon={<Terrain />}
                      label={`${displayRoute.segments} segments`}
                      color="info"
                    />
                  </Stack>

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>Elevation Profile:</strong>
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Typography variant="caption">
                        Min: {displayRoute.minElevation.toFixed(0)}m
                      </Typography>
                      <Typography variant="caption">
                        Max: {displayRoute.maxElevation.toFixed(0)}m
                      </Typography>
                      <Typography variant="caption">
                        Gain: +{displayRoute.elevationGain.toFixed(0)}m
                      </Typography>
                      <Typography variant="caption">
                        Loss: -{displayRoute.elevationLoss.toFixed(0)}m
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Help Text */}
      {availableRoutes.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No routes available. Upload a route file (.fit or .gpx) in the Route Management section first.
        </Alert>
      )}
    </Box>
  );
};

export default RouteAssignment;